import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type {
  ApiError, AuditLog, PaginatedResponse, Role, User, Team,
  Deploy, DeployLog, DeployStats, Application, Scope, Server, ComponentHealth,
  Setting, Registry, Image, ImageTag, Policy, Approval, Backup, Restore, BackupStats, FeatureFlag,
  EmergencyRollbackInput, RollbackVersion, RollbackPreview,
  InfraServerOverview, InfraInstance, DeployConflict, RedeployInput,
} from '@/types';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/hooks/useToast';
import i18n from '@/app/i18n';

const API_BASE_URL = import.meta.env['VITE_API_URL'] || '/api/v1';

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // Send httpOnly cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add correlation ID for request tracing
    config.headers['X-Correlation-ID'] = crypto.randomUUID();

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors with token refresh retry
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 - attempt token refresh before logging out
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Don't retry refresh endpoint - it would cause a deadlock
      if (originalRequest.url === '/auth/refresh') {
        return Promise.reject(error.response.data);
      }

      if (isRefreshing) {
        // Another refresh is in progress, wait for it
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await apiClient.post<{
          accessToken: string;
          expiresIn: number;
          user: User;
        }>('/auth/refresh');

        const { accessToken, expiresIn, user } = response.data;
        useAuthStore.getState().setToken(accessToken, expiresIn);
        useAuthStore.getState().setUser(user);

        onTokenRefreshed(accessToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      } catch {
        toast({
          variant: 'destructive',
          title: i18n.t('auth.accessDenied.title'),
          description: i18n.t('auth.sessionExpiredToast'),
        });
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 - forbidden
    if (error.response?.status === 403) {
      toast({
        variant: 'destructive',
        title: i18n.t('common.error'),
        description: i18n.t('errors.forbidden'),
      });
    }

    // Handle network errors
    if (!error.response) {
      return Promise.reject({
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection.',
      });
    }

    // Return API error
    return Promise.reject(error.response.data);
  }
);

// Normalize backend responses that use nested `pagination` into the flat
// PaginatedResponse<T> shape expected by the frontend type system.
function normalizePagination<T>(data: T): T {
  const d = data as Record<string, unknown>;
  if (d && typeof d === 'object' && 'pagination' in d && !('total' in d)) {
    const p = d['pagination'] as Record<string, number>;
    const total = p['total'] ?? 0;
    const pageSize = p['pageSize'] ?? 20;
    return {
      ...d,
      total,
      page: p['page'] ?? 1,
      pageSize,
      totalPages: p['totalPages'] ?? (pageSize > 0 ? Math.ceil(total / pageSize) : 0),
    } as T;
  }
  return data;
}

// API helper functions
export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    apiClient.get<T>(url, { params }).then((res) => normalizePagination(res.data)),

  post: <T>(url: string, data?: unknown) =>
    apiClient.post<T>(url, data).then((res) => res.data),

  put: <T>(url: string, data?: unknown) =>
    apiClient.put<T>(url, data).then((res) => res.data),

  patch: <T>(url: string, data?: unknown) =>
    apiClient.patch<T>(url, data).then((res) => res.data),

  delete: <T>(url: string) =>
    apiClient.delete<T>(url).then((res) => res.data),
};

// Health check
export const healthApi = {
  liveness: () => api.get<{ status: string }>('/health/live'),
  readiness: () => api.get<{ status: string; checks: Record<string, boolean> }>('/health/ready'),
  status: () => api.get<import('@/types').HealthStatus>('/health'),
};

// Auth API
export const authApi = {
  callback: (code: string, redirectUri: string) =>
    api.post<{
      accessToken: string;
      idToken: string;
      expiresIn: number;
      user: User;
      sessionId: string;
    }>('/auth/callback', { code, redirectUri }),

  refresh: () =>
    api.post<{
      accessToken: string;
      idToken: string;
      expiresIn: number;
      user: User;
      sessionId: string;
    }>('/auth/refresh'),

  logout: () => api.post<{ message: string }>('/auth/logout'),

  me: () => api.get<User>('/auth/me'),
};

// Common list params type
type ListParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  [key: string]: unknown;
};

// User API
export const userApi = {
  list: (params?: ListParams) =>
    api.get<PaginatedResponse<User>>('/users', params as Record<string, unknown>),

  get: (id: string) =>
    api.get<User>(`/users/${id}`),

  getMe: () =>
    api.get<User>('/users/me'),

  create: (data: unknown) =>
    api.post<User>('/users', data),

  update: (id: string, data: unknown) =>
    api.put<User>(`/users/${id}`, data),

  updatePreferences: (prefs: unknown) =>
    api.patch<User>('/users/me/preferences', prefs),
};

// Team API
export const teamApi = {
  list: (params?: ListParams) =>
    api.get<PaginatedResponse<Team>>('/teams', params as Record<string, unknown>),

  get: (id: string) =>
    api.get<Team>(`/teams/${id}`),

  create: (data: { name: string; displayName: string; description?: string; applicationIds?: string[] }) =>
    api.post<Team>('/teams', data),

  update: (id: string, data: { displayName?: string; description?: string; applicationIds?: string[] }) =>
    api.put<Team>(`/teams/${id}`, data),

  delete: (id: string) =>
    api.delete<void>(`/teams/${id}`),

  addApplication: (teamId: string, appId: string) =>
    api.post<Team>(`/teams/${teamId}/applications`, { applicationId: appId }),

  removeApplication: (teamId: string, appId: string) =>
    api.delete<Team>(`/teams/${teamId}/applications/${appId}`),
};

// Role API
export const roleApi = {
  list: (params?: ListParams) =>
    api.get<PaginatedResponse<Role>>('/roles', params as Record<string, unknown>),

  get: (id: string) =>
    api.get<Role>(`/roles/${id}`),

  create: (data: unknown) =>
    api.post<Role>('/roles', data),

  update: (id: string, data: unknown) =>
    api.put<Role>(`/roles/${id}`, data),

  delete: (id: string) =>
    api.delete<void>(`/roles/${id}`),

  assign: (roleId: string, userId: string) =>
    api.post<{ message: string }>(`/roles/${roleId}/assign`, { userId }),

  remove: (roleId: string, userId: string) =>
    api.post<{ message: string }>(`/roles/${roleId}/remove`, { userId }),

  getAvailablePermissions: () =>
    api.get<{ resource: string; actions: string[]; description: string }[]>('/roles/permissions'),
};

// Audit API
export const auditApi = {
  list: (params?: ListParams) =>
    api.get<PaginatedResponse<AuditLog>>('/audit', params as Record<string, unknown>),

  getByCorrelation: (correlationId: string) =>
    api.get<{ data: AuditLog[] }>(`/audit/correlation/${correlationId}`),
};

// Deploy API
export const deployApi = {
  list: (params?: ListParams) =>
    api.get<PaginatedResponse<Deploy>>('/deploys', params as Record<string, unknown>),

  get: (id: string) =>
    api.get<Deploy>(`/deploys/${id}`),

  create: (data: unknown) =>
    api.post<Deploy>('/deploys', data),

  /** Redespliega la imagen que ya corre (mismo digest): recrea el contenedor y refresca variables y secretos. */
  redeploy: (input: RedeployInput) =>
    api.post<Deploy>('/deploys/redeploy', input),

  /** ¿Hay otro deploy activo/pendiente para esta app + scope + servidores? */
  checkConflicts: (applicationId: string, scopeId: string, serverIds: string[]) =>
    api.get<DeployConflict>('/deploys/conflicts', { applicationId, scopeId, serverIds: serverIds.join(',') }),

  start: (id: string) =>
    api.post<void>(`/deploys/${id}/start`),

  cancel: (id: string, reason?: string) =>
    api.post<void>(`/deploys/${id}/cancel`, { reason }),

  approve: (id: string, comment?: string) =>
    api.post<void>(`/deploys/${id}/approve`, { comment }),

  reject: (id: string, reason: string) =>
    api.post<void>(`/deploys/${id}/reject`, { reason }),

  rollback: (id: string, reason?: string) =>
    api.post<Deploy>(`/deploys/${id}/rollback`, { reason }),

  emergencyRollback: (input: EmergencyRollbackInput) =>
    api.post<{ message: string; rollback: Deploy }>('/deploys/emergency-rollback', input),

  getRollbackTarget: (appId: string, scopeId: string) =>
    api.get<{ target: Deploy }>(`/apps/${appId}/scopes/${scopeId}/rollback-target`),

  getRollbackableVersions: (appId: string, scopeId: string, limit?: number) =>
    api.get<{ data: RollbackVersion[]; total: number }>(
      `/apps/${appId}/scopes/${scopeId}/rollback-versions`,
      { limit: limit ?? 10 }
    ),

  previewRollback: (id: string) =>
    api.get<RollbackPreview>(`/deploys/${id}/rollback-preview`),

  getRollbackHistory: (appId: string, scopeId: string, limit?: number) =>
    api.get<{ data: Deploy[]; total: number }>(
      `/apps/${appId}/scopes/${scopeId}/rollback-history`,
      { limit: limit ?? 20 }
    ),

  getLogs: (id: string, params?: Record<string, unknown>) =>
    api.get<{ data: DeployLog[]; total: number }>(`/deploys/${id}/logs`, params).then(res => res.data),

  getStats: (params?: Record<string, unknown>) =>
    api.get<DeployStats>('/deploys/stats', params),

  getLatest: (appId: string, scopeId: string) =>
    api.get<Deploy>(`/apps/${appId}/scopes/${scopeId}/latest-deploy`),
};

// Application API
export const appApi = {
  list: (params?: ListParams) =>
    api.get<PaginatedResponse<Application>>('/apps', params as Record<string, unknown>),

  get: (id: string) =>
    api.get<Application>(`/apps/${id}`),

  create: (data: unknown) =>
    api.post<Application>('/apps', data),

  update: (id: string, data: unknown) =>
    api.put<Application>(`/apps/${id}`, data),

  delete: (id: string) =>
    api.delete<void>(`/apps/${id}`),

  // App+Scope secrets (stored in Vault, app-specific per scope)
  getSecretKeys: (appId: string, scopeId: string) =>
    api.get<{ data: string[] }>(`/apps/${appId}/scopes/${scopeId}/secrets`),

  getSecret: (appId: string, scopeId: string, key: string) =>
    api.get<{ key: string; value: string }>(`/apps/${appId}/scopes/${scopeId}/secrets/${key}`),

  setSecret: (appId: string, scopeId: string, key: string, value: string) =>
    api.put<{ message: string }>(`/apps/${appId}/scopes/${scopeId}/secrets/${key}`, { value }),

  deleteSecret: (appId: string, scopeId: string, key: string) =>
    api.delete<void>(`/apps/${appId}/scopes/${scopeId}/secrets/${key}`),
};

// Scope API
export const scopeApi = {
  list: (params?: ListParams) =>
    api.get<PaginatedResponse<Scope>>('/scopes', params as Record<string, unknown>),

  listByOrder: () =>
    api.get<{ data: Scope[] }>('/scopes/order'),

  get: (id: string) =>
    api.get<Scope>(`/scopes/${id}`),

  create: (data: unknown) =>
    api.post<Scope>('/scopes', data),

  update: (id: string, data: unknown) =>
    api.put<Scope>(`/scopes/${id}`, data),

  delete: (id: string) =>
    api.delete<void>(`/scopes/${id}`),

  getVariables: (scopeId: string) =>
    api.get<{ data: Record<string, string> }>(`/scopes/${scopeId}/variables`),

  setVariable: (scopeId: string, key: string, value: string) =>
    api.put<{ message: string }>(`/scopes/${scopeId}/variables/${key}`, { value }),

  deleteVariable: (scopeId: string, key: string) =>
    api.delete<void>(`/scopes/${scopeId}/variables/${key}`),

  getSecretKeys: (scopeId: string) =>
    api.get<{ data: string[] }>(`/scopes/${scopeId}/secrets`),

  getSecret: (scopeId: string, key: string) =>
    api.get<{ key: string; value: string }>(`/scopes/${scopeId}/secrets/${key}`),

  setSecret: (scopeId: string, key: string, value: string) =>
    api.put<{ message: string }>(`/scopes/${scopeId}/secrets/${key}`, { value }),

  deleteSecret: (scopeId: string, key: string) =>
    api.delete<void>(`/scopes/${scopeId}/secrets/${key}`),
};

// Server API
export const serverApi = {
  list: (params?: ListParams) =>
    api.get<PaginatedResponse<Server>>('/servers', params as Record<string, unknown>),

  get: (id: string) =>
    api.get<Server>(`/servers/${id}`),

  create: (data: unknown) =>
    api.post<Server>('/servers', data),

  update: (id: string, data: unknown) =>
    api.put<Server>(`/servers/${id}`, data),

  delete: (id: string) =>
    api.delete<void>(`/servers/${id}`),

  health: (id: string) =>
    api.get<ComponentHealth>(`/servers/${id}/health`),

  testConnection: (id: string) =>
    api.post<{ success: boolean; message: string }>(`/servers/${id}/test-connection`),

  setMaintenance: (id: string, maintenance: boolean) =>
    api.post<void>(`/servers/${id}/maintenance`, { maintenance }),

  getOnline: () =>
    apiClient.get<{ data: Server[]; total: number }>('/servers/online').then((r) => r.data),
};

// Settings API
export const settingsApi = {
  getAll: (params?: Record<string, unknown>) =>
    api.get<{ data: Setting[] }>('/settings', params),

  get: (key: string) =>
    api.get<Setting>(`/settings/${key}`),

  update: (key: string, value: string) =>
    api.put<Setting>(`/settings/${key}`, { value }),

  updateMultiple: (settings: { key: string; value: string }[]) =>
    api.put<{ data: Setting[] }>('/settings', { settings }),

  reset: (key: string) =>
    api.post<Setting>(`/settings/${key}/reset`),

  resetCategory: (category: string) =>
    api.post<{ message: string }>(`/settings/category/${category}/reset`),

  getCategories: () =>
    api.get<{ data: Array<{ id: string; name: string; description: string }> }>('/settings/categories'),

  validate: (key: string, value: string) =>
    api.post<{ valid: boolean; message?: string }>(`/settings/${key}/validate`, { value }),
};

// Registry API
export const registryApi = {
  list: (params?: ListParams) =>
    api.get<PaginatedResponse<Registry>>('/registries', params as Record<string, unknown>),

  get: (id: string) =>
    api.get<Registry>(`/registries/${id}`),

  create: (data: unknown) =>
    api.post<Registry>('/registries', data),

  update: (id: string, data: unknown) =>
    api.put<Registry>(`/registries/${id}`, data),

  delete: (id: string) =>
    api.delete<void>(`/registries/${id}`),

  testConnection: (id: string) =>
    api.post<{ success: boolean; message: string }>(`/registries/${id}/test-connection`),

  syncImages: (id: string) =>
    api.post<{ message: string }>(`/registries/${id}/sync`),
};

// Image API
export const imageApi = {
  list: (params?: ListParams) =>
    api.get<PaginatedResponse<Image>>('/images', params as Record<string, unknown>),

  get: (id: string) =>
    api.get<Image>(`/images/${id}`),

  getTags: (id: string) =>
    api.get<{ imageId: string; tags: ImageTag[]; total: number }>(`/images/${id}/tags`),

  getStats: (id: string) =>
    api.get<{ pullCount: number; size: number }>(`/images/${id}/stats`),

  markForDeletion: (id: string) =>
    api.delete<void>(`/images/${id}`),

  deleteTag: (imageId: string, tagName: string) =>
    api.delete<void>(`/images/${imageId}/tags/${encodeURIComponent(tagName)}`),

  promoteTag: (imageId: string, sourceTag: string, targetTag = 'latest') =>
    api.post<{ message: string; sourceTag: string; targetTag: string }>(
      `/images/${imageId}/tags/${encodeURIComponent(sourceTag)}/promote`,
      { targetTag },
    ),

  restore: (id: string) =>
    api.post<Image>(`/images/${id}/restore`),

  listByRegistry: (registryId: string, params?: Record<string, unknown>) =>
    api.get<PaginatedResponse<Image>>(`/registries/${registryId}/images`, params),
};

// Policy API
export const policyApi = {
  list: (params?: ListParams) =>
    api.get<PaginatedResponse<Policy>>('/policies', params as Record<string, unknown>),

  get: (id: string) =>
    api.get<Policy>(`/policies/${id}`),

  create: (data: unknown) =>
    api.post<Policy>('/policies', data),

  update: (id: string, data: unknown) =>
    api.put<Policy>(`/policies/${id}`, data),

  delete: (id: string) =>
    api.delete<void>(`/policies/${id}`),

  evaluate: (data: unknown) =>
    api.post<{ allowed: boolean; violations: string[] }>('/policies/evaluate', data),
};

// Approval API
export const approvalApi = {
  list: (params?: Record<string, unknown>) =>
    api.get<PaginatedResponse<Approval>>('/approvals', params),

  getPending: (params?: Record<string, unknown>) =>
    api.get<{ data: Approval[] }>('/approvals/pending', params),

  getHistory: (params?: Record<string, unknown>) =>
    api.get<PaginatedResponse<Approval>>('/approvals/history', params),

  get: (id: string) =>
    api.get<Approval>(`/approvals/${id}`),

  approve: (id: string, comment?: string) =>
    api.post<void>(`/approvals/${id}/approve`, { comment }),

  reject: (id: string, reason: string) =>
    api.post<void>(`/approvals/${id}/reject`, { reason }),

  cancel: (id: string) =>
    api.post<void>(`/approvals/${id}/cancel`),
};

// Backup API
export const backupApi = {
  list: (params?: Record<string, unknown>) =>
    api.get<PaginatedResponse<Backup>>('/backups', params),

  get: (id: string) =>
    api.get<Backup>(`/backups/${id}`),

  create: (data: unknown) =>
    api.post<Backup>('/backups', data),

  delete: (id: string) =>
    api.delete<void>(`/backups/${id}`),

  restore: (id: string, collections?: string[]) =>
    api.post<Restore>(`/backups/${id}/restore`, { collections }),

  getStats: () =>
    api.get<BackupStats>('/backups/stats'),

  cleanup: () =>
    api.post<{ deleted: number }>('/backups/cleanup'),

  listRestores: (params?: Record<string, unknown>) =>
    api.get<PaginatedResponse<Restore>>('/restores', params),

  getRestore: (id: string) =>
    api.get<Restore>(`/restores/${id}`),
};

// Feature Flag API
export const featureFlagApi = {
  list: (params?: ListParams) =>
    api.get<PaginatedResponse<FeatureFlag>>('/feature-flags', params as Record<string, unknown>),

  get: (id: string) =>
    api.get<FeatureFlag>(`/feature-flags/${id}`),

  create: (data: unknown) =>
    api.post<FeatureFlag>('/feature-flags', data),

  update: (id: string, data: unknown) =>
    api.put<FeatureFlag>(`/feature-flags/${id}`, data),

  delete: (id: string) =>
    api.delete<void>(`/feature-flags/${id}`),

  activate: (id: string) =>
    api.post<void>(`/feature-flags/${id}/activate`),

  deactivate: (id: string) =>
    api.post<void>(`/feature-flags/${id}/deactivate`),

  archive: (id: string) =>
    api.post<void>(`/feature-flags/${id}/archive`),
};

// Notification API
export const notificationApi = {
  list: (params?: Record<string, unknown>) =>
    api.get<PaginatedResponse<{ id: string; type: string; title: string; message: string; resourceId: string; resource: string; read: boolean; createdAt: string }>>('/notifications', params),

  getUnreadCount: () =>
    apiClient.get<{ count: number }>('/notifications/unread-count').then((r) => r.data),

  markRead: (id: string) =>
    apiClient.patch(`/notifications/${id}/read`),

  markAllRead: () =>
    apiClient.patch('/notifications/read-all'),
};

// Infrastructure API
export const infrastructureApi = {
  getServerOverview: (serverId: string) =>
    apiClient.get<InfraServerOverview>(`/infrastructure/servers/${serverId}/overview`).then((r) => r.data),

  getScopeInstances: (serverId: string, scopeId: string) =>
    apiClient.get<{ data: InfraInstance[]; total: number }>(`/infrastructure/servers/${serverId}/scopes/${scopeId}/instances`).then((r) => r.data),

  containerAction: (serverId: string, containerId: string, action: string) =>
    apiClient.post(`/infrastructure/servers/${serverId}/containers/${containerId}/action`, { action }),

  getContainerLogs: (serverId: string, containerId: string, tail = 100) =>
    apiClient.get<{ data: string[] }>(`/infrastructure/servers/${serverId}/containers/${containerId}/logs`, { params: { tail } }).then((r) => r.data),
};
